import { apexDomain, go, origin, pathname, search } from '@martincastroalvarez/runtime';
import React, { useEffect, useState } from 'react';
import {
  Action,
  Alert,
  Anubis,
  Heading,
  Link,
  Mark,
  Panel,
  Skeleton,
  Stack,
  Stage,
  Text,
  Working,
} from '@martincastroalvarez/anubis-ui';
import { GoogleSignInButton } from './GoogleSignInButton';
import { useLogin } from './useLogin';
import { useTracker } from '@martincastroalvarez/tracking/useTracker';
import { LoginEvents } from './events';
import { endSession } from '@martincastroalvarez/login/public';
import { createCodeChallenge, createCodeVerifier } from './pkce';
import { useLanguage } from './useLanguage';
import { useBrand, useLoginVideo } from './brand';
import { format } from './i18n';

/**
 * The sign-in page, drawn in anubis and dressed in the tenant's brand (2026-10-05).
 *
 * `Stage` is the page: the brand's login video (`login_video_url`, picked in the brand console) — or
 * the fleet's own footage when it has none, and no footage at all until the live brand has said
 * which (`useLoginVideo`) — under a wash, with the welcome and the brand's line over it; beside it,
 * the half that does the work. The provider buttons say the network's name and
 * nothing else (m7 docs/STYLE.md: one word), each with its mark.
 */
export const Login: React.FC = () => {
  const { verify, verifyLinkedIn, verifyFacebook, verifyTwitter, isLoading, error } = useLogin();
  const [oauthCallbackError, setOauthCallbackError] = useState<string | null>(null);
  const { track } = useTracker();
  // The tenant's language, from the brand — see `useLanguage`. English until it lands.
  const { language, copy, slogan, settled } = useLanguage();
  const brand = useBrand();
  const { video, poster } = useLoginVideo();
  const domain = apexDomain();
  // Both are injected at BUILD time from the tenant's vault, and are empty when the secret is not
  // there — which is what makes each button appear per tenant without a second code path or a
  // runtime config call. Only tenants whose vault holds the credentials get the button, and a
  // button that cannot work is never rendered. See the build:web:* scripts in package.json.
  const facebookClientId = import.meta.env.VITE_META_CLIENT_ID;
  const twitterClientId = import.meta.env.VITE_TWITTER_OAUTH_CLIENT_ID;

  // Landing here ends the current session. The `jwt` cookie is HttpOnly, so only the API can clear
  // it — see `session.ts`. Sign-ins await this same request before they POST.
  useEffect(() => {
    void endSession();
    track({ name: LoginEvents.PAGE_VIEW });
  }, [track]);

  useEffect(() => {
    const params = new URLSearchParams(search());
    const oauthError = params.get('error');
    if (oauthError) {
      const rawDesc = params.get('error_description');
      let oauthDetail = '';
      if (rawDesc) {
        try {
          oauthDetail = decodeURIComponent(rawDesc.replace(/\+/g, ' ')).trim();
        } catch {
          oauthDetail = rawDesc.trim();
        }
      }
      const cancelled =
        oauthError === 'user_cancelled_login' ||
        oauthError === 'user_cancelled_authorize' ||
        oauthError === 'access_denied';
      setOauthCallbackError(
        oauthDetail ||
          (cancelled ? copy.cancelled : format(copy.providerError, { error: oauthError })),
      );
      sessionStorage.removeItem('oauth_provider');
      window.history.replaceState({}, '', `${origin()}${pathname()}`);
      return;
    }
    const code = params.get('code');
    const oauthState = params.get('state');
    if (code && oauthState) {
      const provider = sessionStorage.getItem('oauth_provider') || 'linkedin';
      const stateKey =
        provider === 'facebook'
          ? 'facebook_oauth_state'
          : provider === 'twitter'
            ? 'twitter_oauth_state'
            : 'linkedin_oauth_state';
      const stored = sessionStorage.getItem(stateKey);
      // X alone leaves a second value behind — the PKCE verifier. Read it before the state check
      // and clear it unconditionally: a failed callback must not leave a spent verifier in storage
      // for the next attempt to pick up.
      const codeVerifier = sessionStorage.getItem('twitter_code_verifier');
      sessionStorage.removeItem(stateKey);
      sessionStorage.removeItem('twitter_code_verifier');
      sessionStorage.removeItem('oauth_provider');
      const redirectUri = `${origin()}${pathname()}`;
      window.history.replaceState({}, '', redirectUri);
      if (!stored || oauthState !== stored) {
        setOauthCallbackError(copy.invalidState);
        return;
      }
      if (provider === 'facebook') {
        void verifyFacebook(code, redirectUri);
      } else if (provider === 'twitter') {
        if (!codeVerifier) {
          setOauthCallbackError(copy.incomplete);
          return;
        }
        void verifyTwitter(code, redirectUri, codeVerifier);
      } else {
        void verifyLinkedIn(code, redirectUri);
      }
    }
  }, [verifyLinkedIn, verifyFacebook, verifyTwitter, copy]);

  const handleGoogleError = () => {
    console.error('Google OAuth failed');
    track({ name: LoginEvents.OAUTH_ERROR });
  };

  const startLinkedInLogin = () => {
    setOauthCallbackError(null);
    const clientId = import.meta.env.VITE_LINKEDIN_OAUTH_CLIENT_ID;
    const redirectUri = `${origin()}${pathname()}`;
    const oauthState = crypto.randomUUID();
    sessionStorage.setItem('oauth_provider', 'linkedin');
    sessionStorage.setItem('linkedin_oauth_state', oauthState);
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: redirectUri,
      state: oauthState,
      scope: 'openid profile email',
    });
    go(`https://www.linkedin.com/oauth/v2/authorization?${params.toString()}`);
  };

  const startFacebookLogin = () => {
    setOauthCallbackError(null);
    const redirectUri = `${origin()}${pathname()}`;
    const oauthState = crypto.randomUUID();
    sessionStorage.setItem('oauth_provider', 'facebook');
    sessionStorage.setItem('facebook_oauth_state', oauthState);
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: facebookClientId,
      redirect_uri: redirectUri,
      state: oauthState,
      scope: 'email,public_profile',
    });
    // The fleet's one Graph version (`connect_connector.meta.GRAPH_API_VERSION`): the dialog and the
    // code exchange in `api/oauth_facebook.py` name the same one. v18.0 was retired on 2026-01-26.
    go(`https://www.facebook.com/v21.0/dialog/oauth?${params.toString()}`);
  };

  const startTwitterLogin = async () => {
    setOauthCallbackError(null);
    const redirectUri = `${origin()}${pathname()}`;
    const oauthState = crypto.randomUUID();
    // The verifier is stored and the CHALLENGE is sent. Storing it before navigating away is the
    // point of PKCE — it is the one part of the flow that never travels, so the code X hands back
    // is worthless to anyone who did not start this flow in this browser.
    const codeVerifier = createCodeVerifier();
    const codeChallenge = await createCodeChallenge(codeVerifier);
    sessionStorage.setItem('oauth_provider', 'twitter');
    sessionStorage.setItem('twitter_oauth_state', oauthState);
    sessionStorage.setItem('twitter_code_verifier', codeVerifier);
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: twitterClientId,
      redirect_uri: redirectUri,
      state: oauthState,
      // `users.email` is what makes this login rather than a follow button: the fleet identifies
      // people by email, and without this scope X returns a profile with no address at all.
      // `tweet.read` rides along because X rejects a users.read-only token on /2/users/me.
      scope: 'tweet.read users.read users.email',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });
    go(`https://x.com/i/oauth2/authorize?${params.toString()}`);
  };

  const displayError = error ?? oauthCallbackError;

  const legal = (path: string) => `https://legal.${domain}/${path}`;

  const welcome = (
    <Stack gap="lg" align="center" text="center">
      <Heading level={1} size="hero" align="center">
        {copy.welcome}
      </Heading>
      {slogan ? (
        <Text size="lead" tone="muted" align="center" measure="tight">
          {slogan}
        </Text>
      ) : (
        <Stack gap="xs" align="center">
          <Text size="lead" tone="muted" align="center">
            {copy.epigraphFirst}
          </Text>
          <Text size="lead" tone="muted" align="center">
            {copy.epigraphSecond}
          </Text>
        </Stack>
      )}
      <Text size="small" tone="muted" align="center">
        {domain}
      </Text>
    </Stack>
  );

  return (
    <Anubis brand={brand} title={format(copy.documentTitle, { domain })} language={language}>
      <Stage video={video} poster={poster} show={welcome}>
        <Panel width="tight" pad="fluid">
          <Stack gap="xl" align="stretch" text="center">
            <Stack gap="md" align="center" text="center">
              <Mark
                src={brand?.logo_url || `https://icons.${domain}/login/android-chrome-192x192.png`}
                alt={brand?.title ?? ''}
                size="lg"
                halo
              />
              <Heading level={2} size="h2" align="center">
                {copy.signIn}
              </Heading>
              <Text tone="muted" align="center">
                {copy.signInSubtitle}
              </Text>
            </Stack>
            {/* From the provider's answer until the next app paints — the navigation keeps this page up,
                so `isLoading` is never cleared on success and the ring turns until the page is gone. */}
            {isLoading ? (
              <Working label={copy.verifying} />
            ) : (
              <Stack gap="sm">
                {/* Drawn once the language is final: Google rebuilds its frame when `locale` changes,
                    and a rebuild is a moment the button cannot be pressed. */}
                {settled ? (
                  <GoogleSignInButton
                    label={copy.withGoogle}
                    locale={language}
                    onCredential={(idToken) => void verify(idToken)}
                    onError={handleGoogleError}
                  />
                ) : (
                  <Skeleton shape="button" />
                )}
                <Action variant="ghost" block icon="linkedin" onClick={startLinkedInLogin}>
                  {copy.withLinkedIn}
                </Action>
                {facebookClientId ? (
                  <Action variant="ghost" block icon="facebook" onClick={startFacebookLogin}>
                    {copy.withFacebook}
                  </Action>
                ) : null}
                {twitterClientId ? (
                  <Action variant="ghost" block icon="x" onClick={() => void startTwitterLogin()}>
                    {copy.withX}
                  </Action>
                ) : null}
              </Stack>
            )}
            {displayError ? <Alert boxed>{displayError}</Alert> : null}
            <Text size="small" tone="muted" align="center">
              {copy.legalPrefix}{' '}
              <Link href={legal('service.html')} underline="always">
                {copy.termsOfService}
              </Link>{' '}
              {copy.legalJoin}{' '}
              <Link href={legal('privacy.html')} underline="always">
                {copy.privacyPolicy}
              </Link>
            </Text>
          </Stack>
        </Panel>
      </Stage>
    </Anubis>
  );
};
